import { useEffect, useState } from "react";
import ProfileService from "../../../../../services/Profile";

import {
    FaCoins,
    FaBitcoin,
    FaDollarSign,
    FaTrophy,
    FaGem,
    FaRocket,
    FaCrown,
    FaStar,
} from "react-icons/fa6";

const THEMES = {
    amber: {
        bgColor: "bg-gradient-to-br from-amber-50 to-orange-50",
        bgIcon: "bg-gradient-to-br from-amber-400 to-orange-500",
        bgLabel: "bg-amber-500",
        bgSpan: "bg-amber-200/20",
    },
    blue: {
        bgColor: "bg-gradient-to-br from-blue-50 to-indigo-50",
        bgIcon: "bg-gradient-to-br from-blue-400 to-indigo-500",
        bgLabel: "bg-rose-500",
        bgSpan: "bg-blue-200/20",
    },
    purple: {
        bgColor: "bg-gradient-to-br from-purple-50 to-pink-50",
        bgIcon: "bg-gradient-to-br from-purple-400 to-pink-500",
        bgLabel: "bg-purple-500",
        bgSpan: "bg-purple-200/20",
    },
    emerald: {
        bgColor: "bg-gradient-to-br from-emerald-50 to-teal-50",
        bgIcon: "bg-gradient-to-br from-emerald-400 to-teal-500",
        bgLabel: "bg-emerald-500",
        bgSpan: "bg-emerald-200/20",
    },
    gray: {
        bgColor: "bg-gradient-to-br from-gray-50 to-gray-100",
        bgIcon: "bg-gradient-to-br from-gray-400 to-gray-500",
        bgLabel: "bg-gray-500",
        bgSpan: "bg-gray-200/20",
    },
};

const ICONS = {
    coins: { Icon: FaCoins, theme: "amber" },
    bitcoin: { Icon: FaBitcoin, theme: "amber" },
    trophy: { Icon: FaTrophy, theme: "amber" },
    dollar: { Icon: FaDollarSign, theme: "emerald" },
    gem: { Icon: FaGem, theme: "blue" },
    rocket: { Icon: FaRocket, theme: "blue" },
    crown: { Icon: FaCrown, theme: "purple" },
    star: { Icon: FaStar, theme: "gray" },
};

const getPackageStyle = (pkg) => {
    const entry = ICONS[pkg?.packageIcon];

    return {
        ...THEMES[entry?.theme || "gray"],
        Icon: entry?.Icon || FaStar,
    };
};

export default function SubscribedPackages() {
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(false);

    async function getMySubscribedPackages() {
        try {
            setLoading(true);

            const res = await ProfileService.getMySubscribedPackages();

            if (res?.status === 200) {
                setPackages(res.data?.data || []);
            }
        } catch (error) {
            console.error("Get subscribed packages error:", error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        getMySubscribedPackages();
    }, []);

    const formatPrice = (price) => {
        return new Intl.NumberFormat("en-US").format(price || 0);
    };

    const formatDate = (date) => {
        if (!date) return "—";

        return new Date(date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    if (loading) {
        return (
            <p className="text-center text-gray-500 py-6">
                Loading...
            </p>
        );
    }

    return (
        <section className="flex flex-col gap-4">

            <h2 className="text-lg font-bold text-gray-900">
                My Subscribed Packages
            </h2>

            {packages.length === 0 ? (
                <p className="text-center text-gray-500 py-10">
                    You haven't subscribed to any packages yet.
                </p>
            ) : (
                <div className="grid grid-cols-2 gap-3 mb-24">

                    {packages.map((pkg) => {
                        const s = getPackageStyle(pkg);

                        return (
                            <div
                                key={pkg?._id}
                                className={`relative overflow-hidden min-w-0 ${s.bgColor} border border-gray-200 rounded-2xl shadow-sm p-3 flex flex-col justify-between`}
                            >
                                {/* Background Shapes */}

                                <span
                                    className={`absolute top-0 right-0 w-24 h-24 ${s.bgSpan} rounded-full -translate-y-12 translate-x-12`}
                                />

                                <span
                                    className={`absolute bottom-0 left-0 w-20 h-20 ${s.bgSpan} rounded-full translate-y-10 -translate-x-10`}
                                />

                                <div className="relative z-10">

                                    {/* Header */}

                                    <div className="flex justify-between items-start gap-2 mb-3">

                                        <div className={`${s.bgIcon} w-11 h-11 rounded-xl flex items-center justify-center shadow-md shrink-0`}>
                                            <s.Icon className="text-xl text-white" />
                                        </div>

                                        {pkg?.packageBadge && (
                                            <span className={`${s.bgLabel} text-white text-[10px] px-2 py-1 font-bold rounded-full truncate`}>
                                                {pkg.packageBadge}
                                            </span>
                                        )}

                                    </div>

                                    {/* Title */}

                                    <h3 className="text-sm font-bold text-gray-900 line-clamp-1">
                                        {pkg?.name}
                                    </h3>

                                    <p className="text-gray-600 text-xs mt-1 line-clamp-2 min-h-8">
                                        {pkg?.description}
                                    </p>

                                    {/* Features */}

                                    {pkg?.packageFeatures?.length > 0 && (
                                        <div className="mt-3 space-y-1">

                                            {pkg.packageFeatures.slice(0, 3).map((feature, index) => (
                                                <div
                                                    key={index}
                                                    className="flex items-center gap-1.5"
                                                >
                                                    <span className="text-emerald-600 text-xs">
                                                        ✓
                                                    </span>

                                                    <span className="text-gray-600 text-[10px] line-clamp-1">
                                                        {feature}
                                                    </span>
                                                </div>
                                            ))}

                                        </div>
                                    )}

                                </div>

                                {/* Price */}

                                <div className="relative z-10 mt-4">

                                    <div className="bg-white/70 backdrop-blur-sm rounded-xl p-2.5">

                                        <p className="text-[10px] text-gray-500">
                                            Price
                                        </p>

                                        <p className="font-bold text-gray-900 text-base mt-1">
                                            {formatPrice(pkg?.price)}
                                        </p>

                                    </div>

                                </div>

                                {/* Subscription Status */}

                                <div className="relative z-10 mt-3">

                                    <div className="flex items-center justify-between gap-1">

                                        <span className={`text-[10px] font-semibold px-2 py-1 rounded-full ${
                                            pkg?.isSubscriptionExpired
                                                ? "bg-red-100 text-red-700"
                                                : "bg-green-100 text-green-700"
                                        }`}>
                                            {pkg?.isSubscriptionExpired
                                                ? "Expired"
                                                : "Active"}
                                        </span>

                                    </div>

                                    <div className="mt-2">

                                        <p className="text-[10px] text-gray-500">
                                            Expires on
                                        </p>

                                        <p className="text-xs font-semibold text-gray-800 mt-0.5">
                                            {formatDate(pkg?.subscriptionExpiration)}
                                        </p>

                                    </div>

                                </div>

                            </div>
                        );
                    })}

                </div>
            )}

        </section>
    );
}